/* Automated Quality Response — persistent rule failures, escalated to incidents.
 *
 * Structure and copy follow the Claude Design reference (`autonomous_block.html`) closely: the
 * heavy 2px rule under the header, the plain untitled banners, the dialog's shorter copy, "Detection
 * summary" over the tiles and "Detection log" over the table, the "Bounds"/"7 to 365 days" wording
 * on the locked period card, and the detail drawer's three labelled blocks (Rationale, Incident,
 * Alert delivery). What is NOT copied from the reference is its placeholder data: every `{{ }}` in
 * that file is filled here from the real `/v1/ai/autonomous` responses, including three states the
 * static mock never had to represent — a tri-state permission banner, an absent rule name or run
 * count on a row carried over from the design this module replaced, and a threshold of zero on a
 * reversal recorded under that same design.
 *
 * Five judgements worth not reversing.
 *
 * **"Would have escalated" is its own outcome, and the count a reviewer reads first.** Those are
 * the rows the gate permitted and the mode withheld — precisely what will escalate the day
 * escalation is turned on. Folding them into either neighbour would bury the only rows an
 * observation period is really about.
 *
 * **"Alerts not routed" is the one tile allowed a colour.** An incident that reached nobody looks
 * identical to a healthy estate on every other surface, so that is where the amber earns itself.
 *
 * **Every clock figure comes from the API.** `observation_ends_at`, `days_remaining` and
 * `can_enable` are computed server-side from a period the substrate bounds, so the screen cannot
 * disagree with the door about whether it is open — and the door is a CHECK regardless of what any
 * client renders.
 *
 * **The period card and the not-permitted banner read two different facts.** Whether the viewer may
 * change anything is a *permission*; whether the period is still editable is a *lifecycle*
 * question, closed once escalation is on. Folding them into one flag makes the note about needing
 * an administrator appear to somebody who has just used the button.
 *
 * **A row carried over from this module's earlier design says so, rather than printing zeros.** That
 * design stored a confidence and a rationale and nothing to reconstruct run counts or a rule name
 * from, so a raw render reads `0 of 0 failing` beside an 83% confidence — an arithmetic the platform
 * could not have performed. `evidenceRecorded`/`ruleLabel`/`thresholdRecorded` name the absence
 * instead of asserting it as a measurement.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { can, useIdentity } from '@/entities/session';
import type { Detection, ModeReport } from '@/shared/api/generated/ai-autonomous';
import { useCursorStack } from '@/shared/api/paging';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

import {
  useCapabilities,
  useConfigureObservationPeriod,
  useDetectionPage,
  useEnableEscalation,
  useMode,
  useSummary,
} from '../api/autonomous';
import {
  alertReading,
  alertTone,
  canSavePeriod,
  clearedThreshold,
  dayOf,
  evidenceRecorded,
  formatConfidence,
  outcomeOf,
  outcomeTone,
  periodChangeOf,
  periodEdit,
  periodIsRefusable,
  progressPercent,
  ruleLabel,
  talliesOf,
  thresholdRecorded,
} from '../model/autonomous';
import type { AlertTone, OutcomeTone } from '../model/autonomous';

/** The permission that gates both controls. Admin-only server-side; hiding them here only avoids
 * offering a button a click would be refused for. */
const CONFIGURE_PERMISSION = 'configure:ai.autonomous';

const OUTCOME_TONES: Record<OutcomeTone, BadgeTone> = {
  acted: 'warn',
  /* Info, not ok: a decision that would have escalated is not a healthy state to skim past. */
  pending: 'info',
  held: 'neutral',
};

const ALERT_TONES: Record<AlertTone, BadgeTone> = {
  ok: 'ok',
  warn: 'warn',
  none: 'neutral',
};

function instant(iso: string): string {
  return new Date(iso).toLocaleString();
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

/* ------------------------------------------------------------------------- the posture strip */

function Posture({
  report,
  editable,
}: {
  readonly report: ModeReport;
  readonly editable: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const enable = useEnableEscalation();
  const now = new Date();
  const escalating = !report.observing;
  const remaining = report.days_remaining;

  return (
    <>
      <div className="lq-au__posture">
        <div className="lq-au__postureState">
          <StatusBadge
            tone={escalating ? 'warn' : 'info'}
            label={t(
              escalating ? 'autonomous.posture.active' : 'autonomous.posture.observing',
            )}
          />
          <div className="lq-au__postureMeans">
            {t(
              escalating
                ? 'autonomous.posture.activeMeans'
                : 'autonomous.posture.observingMeans',
            )}
          </div>
        </div>
        <div className="lq-au__postureFacts">
          <dl className="lq-au__facts">
            <div>
              <dt className="lq-au__factKey">{t('autonomous.fact.started')}</dt>
              <dd className="lq-au__factValue">{day(report.shadow_started_at)}</dd>
            </div>
            <div>
              <dt className="lq-au__factKey">{t('autonomous.fact.period')}</dt>
              <dd className="lq-au__factValue">
                {t('autonomous.fact.days', { count: report.observation_days })}
              </dd>
            </div>
            <div>
              <dt className="lq-au__factKey">
                {t(escalating ? 'autonomous.fact.ended' : 'autonomous.fact.ends')}
              </dt>
              <dd className="lq-au__factValue">{day(report.observation_ends_at)}</dd>
            </div>
            {escalating ? (
              <div>
                <dt className="lq-au__factKey">{t('autonomous.fact.since')}</dt>
                {/* The enablement instant, not a remaining figure: once escalating there is no
                    window left to run, so a countdown would be a number with no meaning. */}
                <dd className="lq-au__factValue lq-au__factValue--on">
                  {report.escalation_enabled_at === null
                    ? '—'
                    : day(report.escalation_enabled_at)}
                </dd>
              </div>
            ) : (
              <div>
                <dt className="lq-au__factKey">{t('autonomous.fact.remaining')}</dt>
                <dd
                  className={
                    remaining === 0
                      ? 'lq-au__factValue lq-au__factValue--due'
                      : 'lq-au__factValue'
                  }
                >
                  {remaining === 0
                    ? t('autonomous.fact.none')
                    : t('autonomous.fact.days', { count: remaining })}
                </dd>
              </div>
            )}
          </dl>

          {!escalating && remaining > 0 && (
            <>
              <div className="lq-au__track">
                <div
                  className="lq-au__fill"
                  style={{ width: `${String(progressPercent(report, now))}%` }}
                />
              </div>
              <div className="lq-au__barNote">
                {t('autonomous.bar.dayOf', {
                  day: dayOf(report, now),
                  total: report.observation_days,
                })}
              </div>
            </>
          )}

          {!escalating && remaining === 0 && (
            <>
              <div className="lq-au__ready">{t('autonomous.ready')}</div>
              {/* The button only when the platform says the door is open, and its own refusal
                  when it does not — so a control and a reason it would fail are never both on
                  screen. */}
              {report.can_enable && editable && (
                <Button
                  onClick={() => {
                    setConfirming(true);
                  }}
                >
                  {t('autonomous.enable')}
                </Button>
              )}
              {!report.can_enable && report.enable_blocked_reason !== null && (
                <div className="lq-au__barNote">{report.enable_blocked_reason}</div>
              )}
            </>
          )}

          {escalating && (
            <div className="lq-au__active">
              {t('autonomous.activeNote', {
                on:
                  report.escalation_enabled_at === null
                    ? '—'
                    : day(report.escalation_enabled_at),
                by: report.enabled_by ?? '—',
                count: report.observation_days,
              })}
            </div>
          )}
        </div>
      </div>

      {confirming && (
        <div className="lq-au__dialog">
          <div className="lq-au__dialogTitle">{t('autonomous.confirm.title')}</div>
          <div className="lq-au__dialogBody">{t('autonomous.confirm.body')}</div>
          {enable.isError && (
            <Alert tone="error" title={t('autonomous.confirm.failed')}>
              {enable.error.message}
            </Alert>
          )}
          <div className="lq-au__dialogRow">
            <Button
              tone="secondary"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('autonomous.confirm.cancel')}
            </Button>
            <Button
              busy={enable.isPending}
              onClick={() => {
                enable.mutate(undefined, {
                  onSuccess: () => {
                    setConfirming(false);
                  },
                });
              }}
            >
              {t('autonomous.enable')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------- the observation period */

/** "Last changed {date} by {actor}", or nothing — never fabricated for a tenant that has always
 * been on the default. The same line on both the editable and the locked card, because the
 * question it answers ("who set this") does not stop mattering once the period is frozen. */
function PeriodAudit({ report }: { readonly report: ModeReport }): ReactElement | null {
  const { t } = useTranslation();
  const change = periodChangeOf(report);
  if (change === null) return null;
  return (
    <p className="lq-au__periodNote">
      {t('autonomous.period.lastChanged', { date: day(change.at), by: change.by })}
    </p>
  );
}

function Period({
  report,
  editable,
}: {
  readonly report: ModeReport;
  readonly editable: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [raw, setRaw] = useState(String(report.observation_days));
  const save = useConfigureObservationPeriod();
  const edit = periodEdit(raw, report);
  const bad = periodIsRefusable(edit);

  if (!editable) {
    return (
      <section className="lq-au__card">
        <h2 className="lq-au__cardHead">{t('autonomous.period.title')}</h2>
        <div className="lq-au__locked">
          <div>
            <div className="lq-au__factKey">{t('autonomous.period.configured')}</div>
            <div className="lq-au__factValue">
              {t('autonomous.fact.days', { count: report.observation_days })}
            </div>
          </div>
          <div>
            <div className="lq-au__factKey">{t('autonomous.period.bounds')}</div>
            <div className="lq-au__factValue">
              {t('autonomous.period.boundsValue', {
                min: report.min_observation_days,
                max: report.max_observation_days,
              })}
            </div>
          </div>
        </div>
        <p className="lq-au__periodNote">
          {/* Two different reasons it is read-only, and they are not interchangeable: a closed
              lifecycle once escalation is on, or a permission the viewer does not hold. */}
          {t(
            report.observing ? 'autonomous.period.adminOnly' : 'autonomous.period.closed',
          )}
        </p>
        <PeriodAudit report={report} />
      </section>
    );
  }

  return (
    <section className="lq-au__card">
      <h2 className="lq-au__cardHead">{t('autonomous.period.title')}</h2>
      <p className="lq-au__cardBody">{t('autonomous.period.body')}</p>
      <div className="lq-au__periodRow">
        <div className="lq-au__periodField">
          <div
            className={
              bad ? 'lq-au__periodInput lq-au__periodInput--bad' : 'lq-au__periodInput'
            }
          >
            <input
              value={raw}
              inputMode="numeric"
              aria-label={t('autonomous.period.title')}
              aria-invalid={bad}
              onChange={(event) => {
                setRaw(event.target.value);
              }}
            />
            <span className="lq-au__periodUnit">{t('autonomous.period.unit')}</span>
          </div>
          <div
            className={
              bad ? 'lq-au__periodHelp lq-au__periodHelp--bad' : 'lq-au__periodHelp'
            }
          >
            {edit.kind === 'would-shorten'
              ? t('autonomous.period.cannotShorten', { count: edit.current })
              : t('autonomous.period.range', {
                  min: report.min_observation_days,
                  max: report.max_observation_days,
                })}
          </div>
        </div>
        <Button
          tone="secondary"
          disabled={!canSavePeriod(edit)}
          busy={save.isPending}
          onClick={() => {
            if (canSavePeriod(edit)) save.mutate(Number.parseInt(raw, 10));
          }}
        >
          {t('autonomous.period.save')}
        </Button>
      </div>
      {save.isError && (
        <Alert tone="error" title={t('autonomous.period.failed')}>
          {save.error.message}
        </Alert>
      )}
      <p className="lq-au__periodNote">{t('autonomous.period.noRestart')}</p>
      <PeriodAudit report={report} />
    </section>
  );
}

/* --------------------------------------------------------------------------------- the tiles */

function Tiles({ observing }: { readonly observing: boolean }): ReactElement | null {
  const { t } = useTranslation();
  const summary = useSummary();
  if (summary.data === undefined) return null;
  const counts = talliesOf(summary.data);

  const tiles = [
    {
      key: 'persistent',
      value: counts.persistent,
      note: t('autonomous.tile.persistentNote'),
      warn: false,
    },
    {
      key: 'incidents',
      value: counts.incidents,
      note: t(
        observing
          ? 'autonomous.tile.incidentsObserving'
          : 'autonomous.tile.incidentsNote',
      ),
      warn: false,
    },
    {
      key: 'notified',
      value: counts.notified,
      note: t(observing ? 'autonomous.tile.sentObserving' : 'autonomous.tile.sentNote'),
      warn: false,
    },
    {
      key: 'notRouted',
      value: counts.notRouted,
      note: t('autonomous.tile.notRoutedNote'),
      /* The only tile that may colour, and only when non-zero. */
      warn: counts.notRouted > 0,
    },
  ] as const;

  return (
    <>
      <h2 className="lq-au__section">{t('autonomous.summary')}</h2>
      <div className="lq-au__tiles">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className={tile.warn ? 'lq-au__tile lq-au__tile--warn' : 'lq-au__tile'}
          >
            <div className="lq-au__tileValue">{tile.value.toLocaleString()}</div>
            <div className="lq-au__tileKey">{t(`autonomous.tile.${tile.key}`)}</div>
            <div className="lq-au__tileNote">{tile.note}</div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ----------------------------------------------------------------------------------- the log */

function Row({
  detection,
  open,
  onToggle,
}: {
  readonly detection: Detection;
  readonly open: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const outcome = outcomeOf(detection);
  const reading = alertReading(detection);
  const name = ruleLabel(detection);
  const measured = evidenceRecorded(detection);

  return (
    <>
      <tr
        className={open ? 'lq-au__row lq-au__row--open' : 'lq-au__row'}
        onClick={onToggle}
      >
        <td>
          <StatusBadge
            tone={OUTCOME_TONES[outcomeTone(outcome)]}
            label={t(`autonomous.outcome.${outcome}`)}
            title={t(`autonomous.outcomeMeans.${outcome}`)}
          />
        </td>
        <td>
          {/* The rule's name, never the binding id: a UUID in the cell a steward reads first is
              the raw-id defect this codebase records. An absent name is named rather than left
              blank, because a blank cell reads as a screen that failed. */}
          {name === null ? (
            <span className="lq-muted" title={detection.binding_id}>
              {t('autonomous.ruleUnknown')}
            </span>
          ) : (
            <span className="lq-au__ruleName">{name}</span>
          )}
          {detection.dataset_label !== null && (
            <div className="lq-au__dataset">{detection.dataset_label}</div>
          )}
        </td>
        <td>
          {/* Zeros here would be an arithmetic the platform could not have performed — see
              `evidenceRecorded`. The confidence beside it was recorded and is still shown. */}
          {measured ? (
            <>
              {t('autonomous.evidence', {
                failing: detection.failing_runs,
                runs: detection.runs,
              })}
              <div className="lq-au__sub">
                {t('autonomous.violations', { count: detection.violations })}
              </div>
            </>
          ) : (
            <span className="lq-muted">{t('autonomous.evidenceUnrecorded')}</span>
          )}
        </td>
        <td
          className={
            clearedThreshold(detection) ? 'lq-au__num' : 'lq-au__num lq-au__num--dim'
          }
        >
          {formatConfidence(detection.confidence)}
          {thresholdRecorded(detection) && (
            <div className="lq-au__sub">
              {t('autonomous.needs', {
                threshold: formatConfidence(detection.threshold),
              })}
            </div>
          )}
        </td>
        <td>
          {/* Shortened with the full value on hover. An incident has no name, so this is the
              last-resort case the raw-id rule allows. */}
          {detection.incident_id === null ? (
            <span className="lq-muted">—</span>
          ) : (
            <span className="lq-au__incident" title={detection.incident_id}>
              {t('autonomous.incidentRef', { id: detection.incident_id.slice(0, 8) })}
            </span>
          )}
        </td>
        <td>
          {reading === 'not-attempted' ? (
            <span className="lq-muted">—</span>
          ) : (
            <StatusBadge
              tone={ALERT_TONES[alertTone(reading)]}
              label={t(`autonomous.alert.${reading}`)}
              /* Spread rather than `?? undefined`: under `exactOptionalPropertyTypes` an absent
                 detail and an explicit `undefined` are different things, and only the first is
                 "there is no tooltip". */
              {...(detection.notify_detail === null
                ? {}
                : { title: detection.notify_detail })}
            />
          )}
        </td>
        <td className="lq-au__sub">{instant(detection.detected_at)}</td>
      </tr>
      {open && (
        <tr>
          <td className="lq-au__detailCell" colSpan={7}>
            <div className="lq-au__detail">
              <div className="lq-au__detailKey">{t('autonomous.detail.rationale')}</div>
              {/* Verbatim, as the platform wrote it. No model text exists in this module, and
                  paraphrasing arithmetic would put a second author between the reader and the
                  decision. */}
              <div className="lq-au__rationale">{detection.rationale}</div>
              <div className="lq-au__detailKey">{t('autonomous.detail.incident')}</div>
              <div className="lq-au__detailBody">
                {detection.incident_id === null
                  ? t('autonomous.detail.noIncident')
                  : t('autonomous.detail.incidentSummary', {
                      id: detection.incident_id.slice(0, 8),
                    })}
              </div>
              <div className="lq-au__detailKey">{t('autonomous.detail.alert')}</div>
              <div className="lq-au__detailBody">
                {detection.notify_detail ?? t('autonomous.detail.noAlert')}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Log(): ReactElement {
  const { t } = useTranslation();
  const pager = useCursorStack();
  const page = useDetectionPage(pager.cursor);
  const [open, setOpen] = useState<readonly string[]>([]);

  const columns = [
    { key: 'outcome', label: t('autonomous.column.outcome') },
    { key: 'rule', label: t('autonomous.column.rule') },
    { key: 'evidence', label: t('autonomous.column.evidence') },
    { key: 'confidence', label: t('autonomous.column.confidence'), numeric: true },
    { key: 'incident', label: t('autonomous.column.incident') },
    { key: 'alert', label: t('autonomous.column.alert') },
    { key: 'detected', label: t('autonomous.column.detected') },
  ] as const;

  if (page.isPending) return <SkeletonRows rows={5} columns={7} />;
  if (page.isError) {
    return (
      <Alert tone="error" title={t('autonomous.logFailed')}>
        {page.error.message}
      </Alert>
    );
  }
  if (page.data.detections.length === 0) {
    /* On page 1 this is the healthy state — nothing is failing consistently enough to escalate.
       Past page 1 the walk has simply run out, which is a different sentence. */
    return (
      <EmptyState
        title={t(pager.canGoBack ? 'autonomous.endOfLog' : 'autonomous.nothingFound')}
        guidance={t(
          pager.canGoBack ? 'autonomous.endOfLogNote' : 'autonomous.nothingFoundNote',
        )}
      />
    );
  }

  return (
    <>
      <DataTable columns={columns} caption={t('autonomous.logTitle')}>
        {page.data.detections.map((detection) => (
          <Row
            key={detection.id}
            detection={detection}
            open={open.includes(detection.id)}
            onToggle={() => {
              setOpen((current) =>
                current.includes(detection.id)
                  ? current.filter((id) => id !== detection.id)
                  : [...current, detection.id],
              );
            }}
          />
        ))}
      </DataTable>
      <div className="lq-au__pager">
        {/* A page number and never a total: a keyset listing cannot know how many pages exist
            without counting the whole table, and a denominator would be a truncation wearing a
            fraction. */}
        <span className="lq-au__pageLabel">
          {t('autonomous.pager.page', { page: pager.page })}
        </span>
        <Button tone="secondary" disabled={!pager.canGoBack} onClick={pager.back}>
          {t('autonomous.pager.previous')}
        </Button>
        <Button
          tone="secondary"
          disabled={page.data.nextCursor === null}
          onClick={() => {
            pager.forward(page.data.nextCursor);
          }}
        >
          {t('autonomous.pager.next')}
        </Button>
      </div>
      <p className="lq-au__pagerNote">{t('autonomous.pager.note')}</p>
    </>
  );
}

/* ------------------------------------------------------------------------- the boundary card */

function Boundary(): ReactElement | null {
  const { t } = useTranslation();
  const { data } = useCapabilities();
  if (data === undefined) return null;

  return (
    <>
      <h2 className="lq-au__section lq-au__section--later">
        {t('autonomous.engineTitle')}
      </h2>
      <div className="lq-au__boundary">
        <div className="lq-au__boundaryCols">
          <div className="lq-au__boundaryCol">
            <div className="lq-au__detailKey">{t('autonomous.boundary.does')}</div>
            {data.automatic_actions.map((action) => (
              <div className="lq-au__boundaryItem" key={action}>
                <span className="lq-au__does" aria-hidden="true" />
                <span>{action}</span>
              </div>
            ))}
          </div>
          <div className="lq-au__boundaryCol">
            <div className="lq-au__detailKey">{t('autonomous.boundary.never')}</div>
            {data.never_does.map((entry) => (
              <div className="lq-au__boundaryItem" key={entry}>
                <span className="lq-au__never" aria-hidden="true" />
                <span>{entry}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lq-au__boundaryFoot">{data.model_involvement}</div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------------- the screen */

export function AutonomousLog(): ReactElement {
  const { t } = useTranslation();
  const mode = useMode();
  const summary = useSummary();
  const identity = useIdentity();

  /* **Three-state standing, not a boolean.** `can()` reads false both while the identity query is
   * in flight and once it is denied, so a boolean would flash "you need an administrator" at
   * somebody who is one. Nothing is claimed until the read lands. */
  const standing: 'unknown' | 'forbidden' | 'permitted' =
    identity.isPending || identity.isError
      ? 'unknown'
      : can(identity.data, CONFIGURE_PERMISSION)
        ? 'permitted'
        : 'forbidden';

  const report = mode.data?.report ?? null;
  const observing = report === null || report.observing;
  /* Two facts, kept apart: a *permission* drives the banner, and the *lifecycle* — closed once
     escalation is on — drives whether the card takes input. */
  const editable = standing === 'permitted' && observing;
  const unrouted = (summary.data?.not_routed ?? 0) > 0;

  return (
    <div className="lq-au lq-stack">
      <PageHeader title={t('autonomous.title')} lede={t('autonomous.subtitle')} />
      {/* The reference's one structural device on this screen: a full-width 2px rule under the
          title block, heavier than `PageHeader`'s own hairline (suppressed above via `.lq-au
          .lq-page-header`) because it separates the page's identity from its content. */}
      <div className="lq-pagerule" />

      {standing === 'forbidden' && (
        <Alert tone="notice">{t('autonomous.readOnly.body')}</Alert>
      )}

      {unrouted && (
        <Alert tone="warning">
          {t('autonomous.unrouted.body')}{' '}
          <Link to={SCREEN_PATHS.alertRouting}>{t('autonomous.unrouted.link')}</Link>
        </Alert>
      )}

      {mode.isPending ? (
        <SkeletonRows rows={3} columns={4} />
      ) : mode.isError ? (
        <Alert tone="error" title={t('autonomous.modeFailed')}>
          {mode.error.message}
        </Alert>
      ) : report === null ? (
        /* Never evaluated. A real state rather than an error, and distinct from day 0 — the clock
           starts when detecting starts, so an invented start would make them look the same. */
        <Alert tone="notice" title={t('autonomous.neverEvaluated')}>
          {mode.data.detail ?? t('autonomous.neverEvaluatedFallback')}
        </Alert>
      ) : (
        <>
          <Posture report={report} editable={standing === 'permitted'} />
          {standing !== 'unknown' && <Period report={report} editable={editable} />}
        </>
      )}

      <Tiles observing={observing} />

      <h2 className="lq-au__section">{t('autonomous.logTitle')}</h2>
      <p className="lq-au__sectionNote">{t('autonomous.logNote')}</p>
      {/* **`isSuccess`, not `report === null`.** A *failed* posture read also leaves `report`
          null, and the earlier version reported that as "nothing has been assessed yet" — a claim
          about the data used to report a failure of a request, on the screen whose subject is not
          confusing absence with measurement. The log is its own route and reads for itself. */}
      {mode.isSuccess && mode.data.report === null ? (
        <EmptyState
          title={t('autonomous.neverEvaluatedLog')}
          guidance={t('autonomous.neverEvaluatedLogNote')}
        />
      ) : (
        <Log />
      )}

      <Boundary />
    </div>
  );
}
